import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, getAuthToken, API_BASE_URL } from '../services/api';

const CreateERISARequest = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    user_inputs: {
      user_name: '',
      user_address: '',
      user_city: '',
      user_state: '',
      user_zip: '',
      user_email: '',
      plan_name: '',
      plan_administrator_name: ''
    },
    employer: {
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      department: '',
      ein: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'US'
      }
    },
    delivery_options: {
      send_copy_to_user: true,
      delivery_method: 'email',
      cc_recipients: [],
      custom_subject: '',
      urgency_level: 'routine'
    },
    metadata: {
      reference_id: '',
      priority: 'normal',
      follow_up_date: ''
    }
  });

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    try {
      setTemplateLoading(true);
      setError(null);
      
      // Use the apiGet helper function from the api service
      const data = await apiGet('/api/v1/templates/erisa-standard');
      setTemplate(data.template);
    } catch (err) {
      // Provide more helpful error messages
      let errorMessage = 'Template loading error: ';
      
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        errorMessage += `Unable to connect to the API server at ${API_BASE_URL}. Please check your internet connection and ensure the backend server is running.`;
      } else if (err.message.includes('401') || err.message.includes('403')) {
        errorMessage += 'Authentication failed. Please refresh the page to get a new token.';
      } else if (err.message.includes('404')) {
        errorMessage += 'Template not found. The API endpoint may have changed.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
      console.error('Template loading error:', err);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleArrayInputChange = (section, field, value) => {
    const arrayValue = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: arrayValue
      }
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    // Required user inputs
    if (!formData.user_inputs.user_name.trim()) errors.push('User name is required');
    if (!formData.user_inputs.user_email.trim()) errors.push('User email is required');
    if (!formData.user_inputs.user_address.trim()) errors.push('User address is required');
    if (!formData.user_inputs.user_city.trim()) errors.push('User city is required');
    if (!formData.user_inputs.user_state.trim() || formData.user_inputs.user_state.length !== 2) {
      errors.push('User state must be a 2-letter code');
    }
    if (!formData.user_inputs.user_zip.trim()) errors.push('User ZIP code is required');
    if (!formData.user_inputs.plan_name.trim()) errors.push('Plan name is required');
    if (!formData.user_inputs.plan_administrator_name.trim()) errors.push('Plan administrator name is required');

    // Required employer info
    if (!formData.employer.company_name.trim()) errors.push('Company name is required');
    if (!formData.employer.email.trim()) errors.push('Employer email is required');

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.user_inputs.user_email)) {
      errors.push('User email format is invalid');
    }
    if (!emailRegex.test(formData.employer.email)) {
      errors.push('Employer email format is invalid');
    }

    // ZIP code validation
    const zipRegex = /^[0-9]{5}(-[0-9]{4})?$/;
    if (!zipRegex.test(formData.user_inputs.user_zip)) {
      errors.push('User ZIP code format is invalid');
    }

    // EIN validation (if provided)
    if (formData.employer.ein && !/^[0-9]{2}-[0-9]{7}$/.test(formData.employer.ein)) {
      errors.push('EIN format should be XX-XXXXXXX');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
  
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('; '));
      return;
    }
  
    try {
      setLoading(true);
      
      // Use the apiPost helper function from the api service
      const result = await apiPost('/api/v1/requests', {
        template_id: 'erisa-standard',
        ...formData
      });
      setSuccess({
        message: 'ERISA document request created successfully!',
        requestId: result.request_id,
        preview: result.email_preview
      });
      
      // Reset form after successful submission
      setFormData({
        user_inputs: {
          user_name: '', user_address: '', user_city: '', user_state: '',
          user_zip: '', user_email: '', plan_name: '', plan_administrator_name: ''
        },
        employer: {
          company_name: '', contact_name: '', email: '', phone: '',
          department: '', ein: '',
          address: { street: '', city: '', state: '', zip_code: '', country: 'US' }
        },
        delivery_options: {
          send_copy_to_user: true, delivery_method: 'email',
          cc_recipients: [], custom_subject: '', urgency_level: 'routine'
        },
        metadata: { reference_id: '', priority: 'normal', follow_up_date: '' }
      });
  
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (templateLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Loading template information...</p>
      </div>
    );
  }

  return (
    <div className="app-wrapper">
      {/* Header Navigation */}
      <header className="app-header">
        <div className="logo">
          <span>ERISA Request</span>
        </div>
        <div className="slingshot-logo">
          <img
            className="slingshot-icon slingshot-icon-img"
            src="https://framerusercontent.com/images/iNkWVnMuTcggSAP4DSVMLhnnh4.png?width=291&height=75"
            alt="Slingshot logo"
            loading="lazy"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="main-container">
        {/* Hero Section */}
        <div className="hero-section">
          <h1 className="hero-headline">
            Request Your <span className="text-orange">ERISA Plan Documents</span> with Ease
          </h1>
          <p className="hero-description">
            Submit a professional request to your employer for ERISA plan documents including 
            Summary Plan Descriptions (SPD), Plan Documents, and insurance certificates.
          </p>
        </div>

        {/* Form Container */}
        <div className="form-container">
          <div className="form-header">
            <h2 className="form-title">Create ERISA Document Request</h2>
            <p className="form-subtitle">
              TEST Fill out the form below to request your plan documents. All fields marked with * are required.
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="message message-success">
              <svg className="message-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{success.message}</h3>
                <p style={{ fontSize: '0.9rem', margin: 0 }}>Request ID: <strong>{success.requestId}</strong></p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="message message-error">
              <svg className="message-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Error</h3>
                <p style={{ fontSize: '0.9rem', margin: 0, whiteSpace: 'pre-wrap' }}>{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Your Information */}
            <div className="form-section">
              <h3 className="section-title">
                <span style={{ marginRight: '0.5rem' }}>👤</span>
                Your Information
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.user_name}
                    onChange={(e) => handleInputChange('user_inputs', 'user_name', e.target.value)}
                    className="form-input"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.user_inputs.user_email}
                    onChange={(e) => handleInputChange('user_inputs', 'user_email', e.target.value)}
                    className="form-input"
                    placeholder="john.doe@example.com"
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">
                    Street Address <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.user_address}
                    onChange={(e) => handleInputChange('user_inputs', 'user_address', e.target.value)}
                    className="form-input"
                    placeholder="123 Main Street"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    City <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.user_city}
                    onChange={(e) => handleInputChange('user_inputs', 'user_city', e.target.value)}
                    className="form-input"
                    placeholder="Denver"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    State <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.user_state}
                    onChange={(e) => handleInputChange('user_inputs', 'user_state', e.target.value.toUpperCase())}
                    className="form-input"
                    maxLength="2"
                    placeholder="CO"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    ZIP Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.user_zip}
                    onChange={(e) => handleInputChange('user_inputs', 'user_zip', e.target.value)}
                    className="form-input"
                    placeholder="80202"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Plan Information */}
            <div className="form-section">
              <h3 className="section-title">
                <span style={{ marginRight: '0.5rem' }}>📋</span>
                Plan Information
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Plan Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.plan_name}
                    onChange={(e) => handleInputChange('user_inputs', 'plan_name', e.target.value)}
                    className="form-input"
                    placeholder="ABC Company 401(k) Plan"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Plan Administrator Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.user_inputs.plan_administrator_name}
                    onChange={(e) => handleInputChange('user_inputs', 'plan_administrator_name', e.target.value)}
                    className="form-input"
                    placeholder="Jane Doe"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Employer Information */}
            <div className="form-section">
              <h3 className="section-title">
                <span style={{ marginRight: '0.5rem' }}>🏢</span>
                Employer Information
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.employer.company_name}
                    onChange={(e) => handleInputChange('employer', 'company_name', e.target.value)}
                    className="form-input"
                    placeholder="ABC Corporation"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Contact Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.employer.email}
                    onChange={(e) => handleInputChange('employer', 'email', e.target.value)}
                    className="form-input"
                    placeholder="hr@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contact Name</label>
                  <input
                    type="text"
                    value={formData.employer.contact_name}
                    onChange={(e) => handleInputChange('employer', 'contact_name', e.target.value)}
                    className="form-input"
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input
                    type="text"
                    value={formData.employer.department}
                    onChange={(e) => handleInputChange('employer', 'department', e.target.value)}
                    className="form-input"
                    placeholder="HR, Benefits, etc."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    value={formData.employer.phone}
                    onChange={(e) => handleInputChange('employer', 'phone', e.target.value)}
                    className="form-input"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">EIN (Optional)</label>
                  <input
                    type="text"
                    value={formData.employer.ein}
                    onChange={(e) => handleInputChange('employer', 'ein', e.target.value)}
                    className="form-input"
                    placeholder="12-3456789"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Options */}
            <div className="form-section">
              <h3 className="section-title">
                <span style={{ marginRight: '0.5rem' }}>📧</span>
                Delivery Options
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Delivery Method</label>
                  <select
                    value={formData.delivery_options.delivery_method}
                    onChange={(e) => handleInputChange('delivery_options', 'delivery_method', e.target.value)}
                    className="form-select"
                  >
                    <option value="email">Email</option>
                    <option value="certified_mail">Certified Mail</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Urgency Level</label>
                  <select
                    value={formData.delivery_options.urgency_level}
                    onChange={(e) => handleInputChange('delivery_options', 'urgency_level', e.target.value)}
                    className="form-select"
                  >
                    <option value="routine">Routine</option>
                    <option value="expedited">Expedited</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label className="form-label">
                    CC Recipients (comma-separated emails)
                  </label>
                  <input
                    type="text"
                    value={formData.delivery_options.cc_recipients.join(', ')}
                    onChange={(e) => handleArrayInputChange('delivery_options', 'cc_recipients', e.target.value)}
                    className="form-input"
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Custom Subject (Optional)</label>
                  <input
                    type="text"
                    value={formData.delivery_options.custom_subject}
                    onChange={(e) => handleInputChange('delivery_options', 'custom_subject', e.target.value)}
                    className="form-input"
                    placeholder="Leave blank to use default subject"
                  />
                </div>
                <div className="form-group full-width">
                  <div className="form-checkbox">
                    <input
                      type="checkbox"
                      id="send_copy"
                      checked={formData.delivery_options.send_copy_to_user}
                      onChange={(e) => handleInputChange('delivery_options', 'send_copy_to_user', e.target.checked)}
                    />
                    <label htmlFor="send_copy">
                      Send a copy of the request to my email
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Options */}
            <div className="form-section">
              <h3 className="section-title">
                <span style={{ marginRight: '0.5rem' }}>⚙️</span>
                Additional Options
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Reference ID (Optional)</label>
                  <input
                    type="text"
                    value={formData.metadata.reference_id}
                    onChange={(e) => handleInputChange('metadata', 'reference_id', e.target.value)}
                    className="form-input"
                    placeholder="Your internal reference"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Follow-up Date</label>
                  <input
                    type="date"
                    value={formData.metadata.follow_up_date}
                    onChange={(e) => handleInputChange('metadata', 'follow_up_date', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading && <span className="animate-spin"></span>}
                {loading ? 'Creating Request...' : 'Create ERISA Request'}
              </button>
            </div>
          </form>
        </div>
      </main>

      {/* Bottom Section */}
      <div className="bottom-section"></div>
    </div>
  );
};

export default CreateERISARequest;